/**
 * Join Program Button Block
 *
 * A simple button block for joining the loyalty program with full WordPress button styling support.
 * Subscribes to the global loyalty preview store for consistent preview across all blocks.
 */
import type { BlockEditProps } from '@wordpress/blocks';
import { registerBlockType } from '@wordpress/blocks';
import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls, RichText } from '@wordpress/block-editor';
import { PanelBody, TextControl, Notice, ToggleControl } from '@wordpress/components';

/**
 * Internal dependencies
 */
import './style.scss';
import { useLoyaltyPreviewState } from '../_shared/store/hooks';
import type { PreviewState } from '../_shared/store/loyalty-preview-store';

// ============================================================================
// Types
// ============================================================================

interface JoinProgramButtonAttributes {
	text: string;
	loginText: string;
	enrolledText: string;
	showFirstName: boolean;
	showLastName: boolean;
	firstNameLabel: string;
	lastNameLabel: string;
	emailLabel: string;
	modalTitle: string;
	submitText: string;
	accountCreationNote: string;
	successTitle: string;
	successMessage: string;
}

type JoinProgramButtonEditProps = BlockEditProps<JoinProgramButtonAttributes>;

interface TextConfig {
	value: string;
	onChange: (value: string) => void;
	placeholder: string;
}

// ============================================================================
// Component
// ============================================================================

registerBlockType('leat/join-program-button', {
	edit: ({ attributes, setAttributes }: JoinProgramButtonEditProps) => {
		// Subscribe to global preview state from sidebar using typed hook
		const previewState = useLoyaltyPreviewState();

		// Apply block props directly to RichText, not a wrapper
		// This is required for RichText to be editable - see https://github.com/WordPress/gutenberg/issues/53498
		const blockProps = useBlockProps({
			className: 'wp-block-button__link wp-element-button',
		}) as Record<string, unknown>;

		// Get current text and onChange handler based on preview state
		const getCurrentTextConfig = (state: PreviewState): TextConfig => {
			switch (state) {
				case 'guest':
					return {
						value: attributes.loginText,
						onChange: (value: string) => setAttributes({ loginText: value }),
						placeholder: __('Add guest text…', 'leat-crm'),
					};
				case 'loggedInNotEnrolled':
					return {
						value: attributes.text,
						onChange: (value: string) => setAttributes({ text: value }),
						placeholder: __('Add logged in text…', 'leat-crm'),
					};
				case 'enrolled':
					return {
						value: attributes.enrolledText,
						onChange: (value: string) => setAttributes({ enrolledText: value }),
						placeholder: __('Add enrolled text…', 'leat-crm'),
					};
			}
		};

		const textConfig = getCurrentTextConfig(previewState);

		return (
			<>
				<InspectorControls>
					<PanelBody title={__('Button Text', 'leat-crm')} initialOpen={true}>
						<TextControl
							label={__('Guest User Text', 'leat-crm')}
							value={attributes.loginText}
							onChange={(loginText) => setAttributes({ loginText })}
							help={__('Shown to logged out users', 'leat-crm')}
						/>
						<TextControl
							label={__('Logged In (Not Enrolled) Text', 'leat-crm')}
							value={attributes.text}
							onChange={(text) => setAttributes({ text })}
							help={__('Shown to logged in users who haven\'t joined', 'leat-crm')}
						/>
						<TextControl
							label={__('Enrolled User Text', 'leat-crm')}
							value={attributes.enrolledText}
							onChange={(enrolledText) => setAttributes({ enrolledText })}
							help={__('Shown when already enrolled (usually hidden)', 'leat-crm')}
						/>

						<Notice status="info" isDismissible={false}>
							<p style={{ margin: 0, fontSize: '12px' }}>
								{__('Button is typically hidden for enrolled users. Use the Loyalty Preview sidebar to switch between states.', 'leat-crm')}
							</p>
						</Notice>
					</PanelBody>

					<PanelBody title={__('Modal Settings', 'leat-crm')} initialOpen={false}>
						<ToggleControl
							label={__('Show first name field', 'leat-crm')}
							checked={attributes.showFirstName}
							onChange={(showFirstName) => setAttributes({ showFirstName })}
						/>
						<ToggleControl
							label={__('Show last name field', 'leat-crm')}
							checked={attributes.showLastName}
							onChange={(showLastName) => setAttributes({ showLastName })}
						/>
						<TextControl
							label={__('Modal title', 'leat-crm')}
							value={attributes.modalTitle}
							onChange={(modalTitle) => setAttributes({ modalTitle })}
						/>
						{attributes.showFirstName && (
							<TextControl
								label={__('First name label', 'leat-crm')}
								value={attributes.firstNameLabel}
								onChange={(firstNameLabel) => setAttributes({ firstNameLabel })}
							/>
						)}
						{attributes.showLastName && (
							<TextControl
								label={__('Last name label', 'leat-crm')}
								value={attributes.lastNameLabel}
								onChange={(lastNameLabel) => setAttributes({ lastNameLabel })}
							/>
						)}
						<TextControl
							label={__('Email label', 'leat-crm')}
							value={attributes.emailLabel}
							onChange={(emailLabel) => setAttributes({ emailLabel })}
						/>
						<TextControl
							label={__('Submit button text', 'leat-crm')}
							value={attributes.submitText}
							onChange={(submitText) => setAttributes({ submitText })}
						/>
						<TextControl
							label={__('Account creation note', 'leat-crm')}
							value={attributes.accountCreationNote}
							onChange={(accountCreationNote) => setAttributes({ accountCreationNote })}
							help={__('Shown to guest users only', 'leat-crm')}
						/>
					</PanelBody>

					<PanelBody title={__('Success', 'leat-crm')} initialOpen={false}>
						<TextControl
							label={__('Success title', 'leat-crm')}
							value={attributes.successTitle}
							onChange={(successTitle) => setAttributes({ successTitle })}
						/>
						<TextControl
							label={__('Success message', 'leat-crm')}
							value={attributes.successMessage}
							onChange={(successMessage) => setAttributes({ successMessage })}
						/>
					</PanelBody>
				</InspectorControls>

				<RichText
					{...blockProps}
					aria-label={__('Button text', 'leat-crm')}
					placeholder={textConfig.placeholder}
					value={textConfig.value}
					onChange={textConfig.onChange}
					// @ts-expect-error -- This is fine to pass
					withoutInteractiveFormatting
					identifier="text"
				/>
			</>
		);
	},
	save: () => null, // Dynamic block
});
